import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Input, Row, Col, Card, CardBody, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { doRequest } from "../utils/helper";
import Loader from "../components/Loader";
import Sidebar from "../components/Sidebar";
import SearchHeader from "../components/SearchHeader";
import AdminFooter from "../components/AdminFooter";
import RcPagination from "rc-pagination";
import localeInfo from "rc-pagination/lib/locale/en_US";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Add some custom styles for the user info cell
const userInfoStyles = {
  userInfoCell: {
    minWidth: '200px',
    maxWidth: '250px'
  },
  userName: {
    fontWeight: 'bold',
    color: '#007bff',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  userEmail: {
    fontSize: '12px',
    color: '#6c757d',
    marginTop: '2px'
  },
  userMobile: {
    fontSize: '12px',
    color: '#6c757d',
    marginTop: '2px'
  }
};

export default function UserDesignsList() {
  const [designsList, setDesignsList] = useState([]);
  const [filteredDesignsList, setFilteredDesignsList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userRole, setUserRole] = useState("");
  
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const userLoginDetails = localStorage.getItem("USER_LOGIN_DETAILS");
    if (userLoginDetails) {
      const user = JSON.parse(userLoginDetails);
      setUserRole(user.type || "");
    }
  }, []);

  const textItemRender = (current, type, element) => {
    if (type === "prev") {
      return (
        <FontAwesomeIcon
          icon={["fas", "chevron-left"]}
          className="font-size-sm"
        />
      );
    }
    if (type === "next") {
      return (
        <FontAwesomeIcon
          icon={["fas", "chevron-right"]}
          className="font-size-sm"
        />
      );
    }
    return element;
  };

  const getDesignsList = useCallback(
    async (page = 1, limit = 10, userId = null) => {
      try {
        setLoading(true);
        let url = `design/list?page=${page}&limit=${limit}`;
        
        // Add user filter for admin users
        if (userRole === "admin" && userId) {
          url += `&filter_user_id=${userId}`;
        }

        const response = await doRequest("", "GET", url);
        
        if (response?.resultFlag === 1) {
          setDesignsList(response.response?.data || []);
          setFilteredDesignsList(response.response?.data || []);
          setTotalRecords(response.response?.pagination?.total || 0);
        } else {
          alert(response?.message || "Something went wrong!");
          setDesignsList([]);
          setFilteredDesignsList([]);
          setTotalRecords(0);
        }
      } catch (error) {
        console.error("Error fetching designs:", error);
        alert("Failed to fetch designs. Please try again.");
        setDesignsList([]);
        setFilteredDesignsList([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [userRole]
  );

  // Search functionality
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredDesignsList(designsList);
      return;
    }

    const searchTerm = query.toLowerCase().trim();
    const filtered = designsList.filter((design) => {
      const designName = (design.design_name || "").toLowerCase();
      const productType = (design.store_data?.product_type || "").toLowerCase();
      const category = (design.store_data?.category || "").toLowerCase();
      const userName = (design.user_name || "").toLowerCase();
      const userEmail = (design.user_email || "").toLowerCase();
      const userMobile = (design.mobile || "").toLowerCase();
      
      return designName.includes(searchTerm) || 
             productType.includes(searchTerm) || 
             category.includes(searchTerm) ||
             userName.includes(searchTerm) ||
             userEmail.includes(searchTerm) ||
             userMobile.includes(searchTerm);
    });
    
    setFilteredDesignsList(filtered);
  }, [designsList]);

  // Handle user filter change
  const handleUserFilterChange = (e) => {
    const userId = e.target.value;
    setFilterUserId(userId);
    setCurrentPage(1);
    getDesignsList(1, itemsPerPage, userId);
  };

  // Clear user filter
  const clearUserFilter = () => {
    setFilterUserId("");
    setCurrentPage(1);
    getDesignsList(1, itemsPerPage, null);
  };

  // Handle pagination
  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setItemsPerPage(pageSize);
    getDesignsList(page, pageSize, filterUserId || null);
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  // Get available sizes
  const getAvailableSizes = (sizeData) => {
    return Object.keys(sizeData || {}).join(', ');
  };

  // Get available colors
  const getAvailableColors = (colors) => {
    return (colors || []).join(', ');
  };

  // Download CSV
  const downloadCSV = (design) => {
    if (design.csv_file_url) {
      const link = document.createElement('a');
      link.href = design.csv_file_url;
      link.download = `${design.design_name}-data.csv`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // View design details
  const viewDesignDetails = (design) => {
    // You can implement a modal or navigate to a details page
    console.log("View design details:", design);
    // alert(`Design: ${design.design_name}\nType: ${design.store_data?.product_type}\nPrice: ${formatPrice(design.store_data?.price)}`);
    navigate(`/Design/${design.id}`);
  };

  // Handle image error
  const handleImageError = (e) => {
    e.target.src = '/assets/images/placeholder-design.png';
  };

  useEffect(() => {
    getDesignsList(currentPage, itemsPerPage, filterUserId || null);
  }, [getDesignsList, currentPage, itemsPerPage, filterUserId]);

  // Update filtered results when designsList changes
  useEffect(() => {
    setFilteredDesignsList(designsList);
  }, [designsList]);

  return (
    <>
      {loading && <Loader />}
      <div className="container-fluid main">
        <Sidebar />
        <section className="home">
          <div className="container-fluid">
            <div className="searcmain_pad">
              <div className="search_header">
                <ul className="search_list">
                  <li>
                    <h3>User Designs</h3>
                  </li>
                  <li>
                    {/* <div className="cansear_div">
                      <button className="can_sea" onClick={() => navigate(-1)}>
                        Back
                      </button>
                    </div> */}
                  </li>
                </ul>
              </div>

              {/* Admin Filter Controls */}
              {userRole === "admin" && (
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="admin-controls">
                      <Input
                        type="number"
                        placeholder="Filter by User ID"
                        value={filterUserId}
                        onChange={handleUserFilterChange}
                        className="filter-input"
                      />
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={clearUserFilter}
                        className="ms-2"
                      >
                        Clear Filter
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Search Header */}
              {/* <SearchHeader
                placeHolder="Search by design name, type, or category..."
                search={searchQuery}
                onChangeHandler={handleSearch}
              /> */}

              {/* Search Results Info */}
              {searchQuery && (
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="search-results-info">
                    <span className="text-muted">
                      Found {filteredDesignsList.length} designs matching "{searchQuery}"
                    </span>
                  </div>
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setFilteredDesignsList(designsList);
                    }}
                  >
                    Clear Search
                  </Button>
                </div>
              )}

              {/* Designs Table */}
              <div className="table-responsive table_user_mar">
                <Table hover striped className="table_cust align-middle">
                  <thead className="thead_bg">
                    <tr>
                      <th className="userth" style={{ width: "120px" }}>Preview</th>
                      <th className="userth">Design Name</th>
                      <th className="userth">Created</th>
                      <th className="userth">User Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDesignsList?.length > 0 ? (
                      filteredDesignsList.map((design, index) => (
                        <tr key={design.id || index}>
                          <td>
                            <div className="design-preview">
                              <img
                                src={design.design_thumbnail_url || '/assets/images/placeholder-design.png'}
                                alt={design.design_name}
                                className="design-thumbnail"
                                onError={handleImageError}
                                style={{
                                  width: '80px',
                                  height: '80px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  border: '1px solid #e0e0e0'
                                }}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="design-name-cell">
                              <strong>{design.design_name}</strong>
                              <br />
                              <small className="text-muted">ID: {design.id}</small>
                            </div>
                          </td>
                         
                          <td>
                            <span className="design-date">
                              {new Date(design.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td style={userInfoStyles.userInfoCell}>
                            <div className="user-info-cell">
                              <div className="user-name">
                                <Button
                                  color="link"
                                  className="p-0 text-decoration-none"
                                  onClick={() => window.open(`/viewprofile/${design.user_id}`, "_blank")}
                                  style={userInfoStyles.userName}
                                >
                                  {design.user_name || 'N/A'}
                                </Button>
                              </div>
                              <div className="user-email" style={userInfoStyles.userEmail}>
                                {design.user_email || 'N/A'}
                              </div>
                              <div className="user-mobile" style={userInfoStyles.userMobile}>
                                {design.mobile || 'N/A'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          {searchQuery 
                            ? "No designs found matching your search criteria." 
                            : "No designs found."
                          }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredDesignsList.length > 0 && !searchQuery && (
                <div className="mt-3 d-flex justify-content-center">
                  <RcPagination
                    showSizeChanger
                    defaultPageSize={itemsPerPage}
                    current={currentPage}
                    total={totalRecords}
                    locale={localeInfo}
                    itemRender={textItemRender}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                    pageSizeOptions={['10', '20', '50', '100']}
                  />
                </div>
              )}

              {/* Summary Info */}
              <div className="mt-3 text-center text-muted">
                <small>
                  Showing {filteredDesignsList.length} of {totalRecords} designs
                  {userRole === "admin" && filterUserId && ` for User ID: ${filterUserId}`}
                </small>
              </div>
            </div>
          </div>
        </section>
      </div>
      <AdminFooter />
    </>
  );
}

